import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronLeft } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import MetaTags from '@/components/MetaTags';
import { useFavorites } from '@/contexts/FavoritesContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Favorites = () => {
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const [favoriteProducts, setFavoriteProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (favorites.size > 0) {
      fetchFavoriteProducts();
    } else {
      setFavoriteProducts([]);
    }
  }, [favorites]);

  const fetchFavoriteProducts = async () => {
    setLoading(true);
    try {
      const favoriteIds = Array.from(favorites);

      if (favoriteIds.length === 0) {
        setFavoriteProducts([]);
        setLoading(false);
        return;
      }

      // Use secure function to fetch products
      const { data: products, error: productsError } = await supabase
        .rpc('get_products_by_ids', { product_ids: favoriteIds });

      if (productsError) throw productsError;

      // Fetch categories and subcategories separately
      const categoryIds = products?.map(p => p.category_id).filter(Boolean) || [];
      const subcategoryIds = products?.map(p => p.subcategory_id).filter(Boolean) || [];

      const [categoriesResult, subcategoriesResult] = await Promise.all([
        categoryIds.length > 0 ? supabase.from('categories').select('id, name').in('id', categoryIds) : { data: [] },
        subcategoryIds.length > 0 ? supabase.from('subcategories').select('id, name').in('id', subcategoryIds) : { data: [] }
      ]);

      // Map categories and subcategories to products
      const categoriesMap = new Map((categoriesResult.data || []).map(c => [c.id, c]));
      const subcategoriesMap = new Map((subcategoriesResult.data || []).map(s => [s.id, s]));

      const enhancedProducts = products?.map(product => ({
        ...product,
        categories: product.category_id ? categoriesMap.get(product.category_id) : null,
        subcategories: product.subcategory_id ? subcategoriesMap.get(product.subcategory_id) : null
      })) || [];

      setFavoriteProducts(enhancedProducts);
    } catch (error) {
      console.error('Error fetching favorite products:', error);
      toast.error('שגיאה בטעינת המוצרים המועדפים');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetaTags
        title="מוצרים מועדפים - Excess Market"
        description="המוצרים המועדפים שלך - כל המוצרים שסימנת כמועדפים במקום אחד"
      />
      <Navigation />
      <div className="min-h-screen bg-background pt-4 lg:pt-8" dir="rtl">
        <div className="container mx-auto px-4 py-2">
          {/* Breadcrumbs */}
          <div className="mb-6 mt-4 lg:mt-0">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    בית
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronLeft className="h-4 w-4" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>מועדפים</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-3 mb-4">
              <Heart className="h-8 w-8 text-red-500" />
              <h1 className="text-3xl font-bold">מועדפים</h1>
            </div>
            <p className="text-muted-foreground">
              {favorites.size > 0 && `יש לך ${favorites.size} מוצרים מועדפים`}
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p>טוען מוצרים מועדפים...</p>
            </div>
          ) : favorites.size === 0 ? (
            <div className="text-center py-12">
              <Heart className="h-24 w-24 mx-auto text-muted-foreground mb-6" />
              <h2 className="text-xl font-semibold mb-4">אין מוצרים מועדפים עדיין</h2>
              <p className="text-muted-foreground mb-6">
                התחל לסמן מוצרים כמועדפים כדי לראות אותם כאן
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/products')}>
                  עיין במוצרים
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favoriteProducts.map((product) => (
                <ProductCard key={product.id} product={product} viewMode="grid" />
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Favorites;